import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";

const TOAST_DURATION = 4000;

const memberApi = {
  list: (vendorProfileId, { q = "", is_active = "" } = {}) => {
    const params = new URLSearchParams();
    if (q)          params.set("q", q);
    if (is_active !== "") params.set("is_active", is_active);
    return axiosInstance.get(
      `/api/superadmin/vendors/${vendorProfileId}/account-members/?${params}`
    );
  },

  getById: (vendorProfileId, memberId) =>
    axiosInstance.get(
      `/api/superadmin/vendors/${vendorProfileId}/account-members/${memberId}/`
    ),

  create: (vendorProfileId, body) =>
    axiosInstance.post(
      `/api/superadmin/vendors/${vendorProfileId}/account-members/`,
      body
    ),

  patch: (vendorProfileId, memberId, body) =>
    axiosInstance.patch(
      `/api/superadmin/vendors/${vendorProfileId}/account-members/${memberId}/`,
      body
    ),

  delete: (vendorProfileId, memberId) =>
    axiosInstance.delete(
      `/api/superadmin/vendors/${vendorProfileId}/account-members/${memberId}/`
    ),
};

let _amToastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _amToastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg) => toast._push({ type: "success", title, msg }),
  error:   (title, msg) => toast._push({ type: "error",   title, msg }),
  info:    (title, msg) => toast._push({ type: "info",    title, msg }),
  warning: (title, msg) => toast._push({ type: "warning", title, msg }),
  loading: (title)      => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id)         => _amToastSetter?.((p) => p.filter((t) => t.id !== id)),
};

const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓" },
  error:   { bar: "#dc2626", icon: "✕" },
  info:    { bar: "#1d4ed8", icon: "i" },
  warning: { bar: "#d97706", icon: "!" },
  loading: { bar: "#94a3b8", icon: null },
};

const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting,  setExiting]  = useState(false);
  const rafRef  = useRef(null);
  const startTs = useRef(Date.now());

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);

  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const pct = Math.max(0, 100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100);
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);

  return (
    <div style={{ position: "relative", overflow: "hidden", background: "#fff", borderRadius: 10, borderLeft: `4px solid ${meta.bar}`, boxShadow: "0 4px 20px rgba(0,0,0,.10), 0 0 0 1px rgba(0,0,0,.06)", minWidth: 300, maxWidth: 360, animation: exiting ? "am-alOut .28s ease forwards" : "am-alIn .28s cubic-bezier(.34,1.56,.64,1) forwards" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "13px 14px 14px" }}>
        <div style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: `${meta.bar}18`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
          {t.type === "loading"
            ? <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ animation: "am-spin .85s linear infinite" }}><circle cx="6.5" cy="6.5" r="5" stroke={meta.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" /></svg>
            : <span style={{ fontSize: 11, fontWeight: 800, color: meta.bar, lineHeight: 1 }}>{meta.icon}</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 650, color: "#0f172a", lineHeight: 1.35 }}>{t.title}</p>
          {t.msg && <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{t.msg}</p>}
        </div>
        <button onClick={dismiss} style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: 2, flexShrink: 0, display: "flex", borderRadius: 4 }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#94a3b8")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#cbd5e1")}>
          <IcoClose />
        </button>
      </div>
      {!t.persistent && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: meta.bar, transition: "width .1s linear", borderRadius: "0 2px 0 0" }} />
        </div>
      )}
    </div>
  );
};

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _amToastSetter = setToasts;
  const dismiss = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
      {toasts.map((t) => <ToastItem key={t.id} t={t} onDismiss={dismiss} />)}
    </div>
  );
};

const IcoClose    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IcoLoader   = ({ size = 18 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: "am-spin 0.8s linear infinite", flexShrink: 0 }}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>;
const IcoUser     = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const IcoUsers    = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IcoPlus     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IcoEdit     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IcoTrash    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
const IcoSearch   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const IcoRefresh  = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>;
const IcoEye      = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const IcoCheck    = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>;
const IcoMail     = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const IcoBriefcase= () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>;
const IcoCalendar = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const IcoShield   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
const IcoAlert    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
const IcoFilter   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>;
const IcoChevronDown = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="6 9 12 15 18 9"/></svg>;

const SkeletonBlock = ({ w, h = 14 }) => (
  <div style={{ height: h, width: w, borderRadius: 6, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "am-shimmer 1.4s infinite" }} />
);

const FieldError = ({ msg }) =>
  msg ? <span style={{ fontSize: 11.5, color: "#dc2626", display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}><IcoAlert />{msg}</span> : null;

const PERM_LABELS = [
  { key: "can_view",   label: "View"   },
  { key: "can_create", label: "Create" },
  { key: "can_update", label: "Update" },
  { key: "can_delete", label: "Delete" },
];

const PermissionBadge = ({ perms }) => (
  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
    {PERM_LABELS.map(({ key, label }) => (
      <span key={key} style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px", borderRadius: 20, fontSize: 11.5, fontWeight: 700,
        background: perms?.[key] ? "#dcfce7" : "#f1f5f9",
        color:      perms?.[key] ? "#16a34a" : "#94a3b8",
      }}>
        {perms?.[key] ? <IcoCheck /> : null}{label}
      </span>
    ))}
  </div>
);

const PermissionGrid = ({ perms, onChange, readOnly = false }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
    {PERM_LABELS.map(({ key, label }) => (
      <div key={key} style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "9px 12px", borderRadius: 8,
        background: perms[key] ? "#f0fdf4" : "#f8fafc",
        border: `1px solid ${perms[key] ? "#bbf7d0" : "#e2e8f0"}`,
        transition: "all .15s",
      }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: perms[key] ? "#166534" : "#64748b", display: "flex", alignItems: "center", gap: 6 }}>
          <IcoShield />{label}
        </span>
        <button type="button" onClick={() => !readOnly && onChange(key, !perms[key])} disabled={readOnly}
          style={{ width: 40, height: 22, borderRadius: 11, border: "none", cursor: readOnly ? "default" : "pointer", background: perms[key] ? "#16a34a" : "#e2e8f0", position: "relative", transition: "background .2s", flexShrink: 0 }}>
          <span style={{ position: "absolute", top: 2, left: perms[key] ? 20 : 2, width: 18, height: 18, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.15)", transition: "left .2s" }} />
        </button>
      </div>
    ))}
  </div>
);

const StatusBadge = ({ active }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px",
    borderRadius: 20, fontSize: 12, fontWeight: 700,
    background: active ? "#dcfce7" : "#f1f5f9",
    color:      active ? "#16a34a" : "#94a3b8",
  }}>
    <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? "#16a34a" : "#94a3b8" }} />
    {active ? "Active" : "Inactive"}
  </span>
);

const AVATAR_COLORS = [
  ["#dbeafe","#1d4ed8"], ["#dcfce7","#16a34a"], ["#fef3c7","#d97706"],
  ["#fce7f3","#be185d"], ["#ede9fe","#6d28d9"], ["#ffedd5","#c2410c"],
];
const getAvatarColor = (name = "") => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const Avatar = ({ name, size = 36 }) => {
  const [bg, fg] = getAvatarColor(name);
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <span style={{ fontSize: size * 0.36, fontWeight: 700, color: fg, lineHeight: 1 }}>
        {(name || "?").slice(0, 2).toUpperCase()}
      </span>
    </div>
  );
};

const ConfirmDeleteModal = ({ member, onConfirm, onCancel, loading }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 }}
    onClick={onCancel}>
    <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 420, padding: 28, boxShadow: "0 24px 64px rgba(0,0,0,.20)", animation: "am-fadeUp .2s ease" }}
      onClick={(e) => e.stopPropagation()}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, textAlign: "center" }}>
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <div>
          <h3 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 700, color: "#0f172a" }}>Remove Member?</h3>
          <p style={{ margin: 0, fontSize: 13.5, color: "#64748b", lineHeight: 1.6 }}>
            <strong style={{ color: "#0f172a" }}>{member?.name}</strong> will be permanently removed as an account member. This action cannot be undone.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 4, width: "100%" }}>
          <button onClick={onCancel} disabled={loading}
            style={{ flex: 1, padding: "10px 0", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", background: "#fff", color: "#374151" }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            style={{ flex: 1, padding: "10px 0", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", background: loading ? "#fca5a5" : "#dc2626", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            {loading ? <><IcoLoader size={14} /> Removing…</> : <><IcoTrash /> Remove</>}
          </button>
        </div>
      </div>
    </div>
  </div>
);

const MemberDetailModal = ({ member, onClose, onEdit }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const InfoRow = ({ icon, label, value }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "1px solid #f8fafc" }}>
      <span style={{ color: "#94a3b8", display: "flex", flexShrink: 0, marginTop: 1 }}>{icon}</span>
      <span style={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 600, minWidth: 140, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 500, flex: 1, wordBreak: "break-word" }}>{value ?? <span style={{ color: "#cbd5e1" }}>—</span>}</span>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 520, boxShadow: "0 24px 64px rgba(0,0,0,.20)", animation: "am-fadeUp .2s ease", maxHeight: "90vh", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}>

        <div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Avatar name={member.name} size={48} />
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{member.name}</h2>
              <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b" }}>{member.designation || "—"}</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <StatusBadge active={member.is_active} />
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 6, borderRadius: 6 }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#64748b")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}>
              <IcoClose />
            </button>
          </div>
        </div>

        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>
          <div style={{ background: "#f8fafc", borderRadius: 10, padding: "4px 12px", marginBottom: 16 }}>
            <InfoRow icon={<IcoUser />}      label="Full Name"     value={member.name} />
            <InfoRow icon={<IcoBriefcase />} label="Designation"   value={member.designation} />
            <InfoRow icon={<IcoMail />}      label="Email"         value={member.email} />
            <InfoRow icon={<IcoCalendar />}  label="Joined"        value={member.created_at} />
            <InfoRow icon={<IcoCalendar />}  label="Last Updated"  value={member.updated_at} />
            <InfoRow icon={<IcoMail />}      label="Created By"    value={member.created_by_email} />
          </div>

          <div style={{ marginBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ color: "#6366f1" }}><IcoShield /></span>
              <h4 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>Permissions</h4>
            </div>
            <PermissionGrid perms={member.permissions || {}} onChange={() => {}} readOnly />
          </div>
        </div>

        <div style={{ padding: "14px 24px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
          <button onClick={onClose}
            style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: "pointer", background: "#fff", color: "#374151" }}>
            Close
          </button>
          <button onClick={() => { onClose(); onEdit(member); }}
            style={{ padding: "9px 20px", border: "none", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: "pointer", background: "#f59e0b", color: "#fff", display: "flex", alignItems: "center", gap: 6 }}>
            <IcoEdit /> Edit Member
          </button>
        </div>
      </div>
    </div>
  );
};

const DEFAULT_PERMS = { can_view: true, can_create: false, can_update: false, can_delete: false };

const MemberFormModal = ({ mode, initialData, vendorProfileId, onClose, onSuccess }) => {
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    name:        initialData?.name        ?? "",
    designation: initialData?.designation ?? "",
    email:       initialData?.email       ?? "",
    password:    "",
    is_active:   initialData?.is_active   ?? true,
    permissions: { ...DEFAULT_PERMS, ...(initialData?.permissions ?? {}) },
  });
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const validate = () => {
    const e = {};
    if (!form.name.trim())        e.name = "Name is required.";
    if (!form.designation.trim()) e.designation = "Designation is required.";
    if (!isEdit) {
      if (!form.email.trim())                              e.email = "Email is required.";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email address.";
      if (!form.password)         e.password = "Password is required.";
      else if (form.password.length < 6) e.password = "Password must be at least 6 characters.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    const loadId = toast.loading(isEdit ? "Updating member…" : "Creating member…");

    try {
      let data;

      if (isEdit) {
        const payload = {
          designation: form.designation,
          is_active:   form.is_active,
          permissions: form.permissions,
        };
        const { data: resData } = await memberApi.patch(vendorProfileId, initialData.id, payload);
        data = resData;
      } else {
        const payload = {
          name:        form.name.trim(),
          designation: form.designation.trim(),
          email:       form.email.trim().toLowerCase(),
          password:    form.password,
          permissions: form.permissions,
        };
        const { data: resData } = await memberApi.create(vendorProfileId, payload);
        data = resData;
      }

      if (data?.status >= 400) {
        const detail = data?.data?.detail || data?.data?.message || "Save failed";
        const fieldErrs = {};
        Object.entries(data?.data || {}).forEach(([k, v]) => {
          if (!["detail", "message", "status"].includes(k))
            fieldErrs[k] = Array.isArray(v) ? v[0] : v;
        });
        if (Object.keys(fieldErrs).length) setErrors(fieldErrs);
        throw new Error(detail);
      }

      toast.dismiss(loadId);
      toast.success(
        isEdit ? "Member Updated" : "Member Created",
        isEdit ? "Account member updated successfully." : "New account member has been added."
      );
      onSuccess(data?.data ?? data);
      onClose();
    } catch (err) {
      toast.dismiss(loadId);
      toast.error(isEdit ? "Update Failed" : "Create Failed", err.message || "An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  };

  const setField = (name, value) => {
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: null }));
  };

  const setPermission = (key, value) =>
    setForm((p) => ({ ...p, permissions: { ...p.permissions, [key]: value } }));

  const inputStyle = (field) => ({
    width: "100%", padding: "9px 12px", border: `1px solid ${errors[field] ? "#fca5a5" : "#e2e8f0"}`,
    borderRadius: 8, fontSize: 13, background: "#fff", color: "#1e293b", outline: "none",
    transition: "border-color .15s", boxSizing: "border-box",
  });

  const Label = ({ children, required }) => (
    <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", letterSpacing: ".03em", display: "block", marginBottom: 4 }}>
      {children}{required && <span style={{ color: "#dc2626" }}> *</span>}
    </label>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 560, boxShadow: "0 24px 64px rgba(0,0,0,.20)", animation: "am-fadeUp .2s ease", display: "flex", flexDirection: "column", maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* <div style={{ width: 36, height: 36, borderRadius: 9, background: isEdit ? "linear-gradient(135deg,#f59e0b,#d97706)" : "linear-gradient(135deg,#6366f1,#3b82f6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {isEdit ? <IcoEdit /> : <IcoPlus />}
            </div> */}
            <div>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{isEdit ? "Edit Member" : "Add Account Member"}</h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>{isEdit ? `Editing ${initialData?.name}` : "Add a new member to this vendor account"}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 6, borderRadius: 6 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#64748b")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}>
            <IcoClose />
          </button>
        </div>

        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1, display: "flex", flexDirection: "column", gap: 14 }}>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <Label required>Full Name</Label>
              <input value={form.name} readOnly={isEdit} onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. John Doe" style={{ ...inputStyle("name"), background: isEdit ? "#f8fafc" : "#fff", color: isEdit ? "#94a3b8" : "#1e293b" }}
                onFocus={(e) => { if (!isEdit) e.target.style.borderColor = errors.name ? "#f87171" : "#3b82f6"; }}
                onBlur={(e) => { e.target.style.borderColor = errors.name ? "#fca5a5" : "#e2e8f0"; }} />
              <FieldError msg={errors.name} />
              {isEdit && <span style={{ fontSize: 11, color: "#94a3b8", marginTop: 3, display: "block" }}>Name cannot be changed</span>}
            </div>
            <div>
              <Label required>Designation</Label>
              <input value={form.designation} onChange={(e) => setField("designation", e.target.value)}
                placeholder="e.g. Senior Buyer" style={inputStyle("designation")}
                onFocus={(e) => { e.target.style.borderColor = errors.designation ? "#f87171" : "#3b82f6"; }}
                onBlur={(e) => { e.target.style.borderColor = errors.designation ? "#fca5a5" : "#e2e8f0"; }} />
              <FieldError msg={errors.designation} />
            </div>
          </div>

          {!isEdit && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label required>Email Address</Label>
                <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)}
                  placeholder="member@example.com" style={inputStyle("email")}
                  onFocus={(e) => { e.target.style.borderColor = errors.email ? "#f87171" : "#3b82f6"; }}
                  onBlur={(e) => { e.target.style.borderColor = errors.email ? "#fca5a5" : "#e2e8f0"; }} />
                <FieldError msg={errors.email} />
              </div>
              <div>
                <Label required>Password</Label>
                <div style={{ position: "relative" }}>
                  <input type={showPwd ? "text" : "password"} value={form.password} onChange={(e) => setField("password", e.target.value)}
                    placeholder="Min. 6 characters" style={{ ...inputStyle("password"), paddingRight: 38 }}
                    onFocus={(e) => { e.target.style.borderColor = errors.password ? "#f87171" : "#3b82f6"; }}
                    onBlur={(e) => { e.target.style.borderColor = errors.password ? "#fca5a5" : "#e2e8f0"; }} />
                  <button type="button" onClick={() => setShowPwd((p) => !p)}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 0 }}>
                    <IcoEye />
                  </button>
                </div>
                <FieldError msg={errors.password} />
              </div>
            </div>
          )}

          {isEdit && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: form.is_active ? "#f0fdf4" : "#f8fafc", borderRadius: 9, border: `1px solid ${form.is_active ? "#bbf7d0" : "#e2e8f0"}`, transition: "all .2s" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: form.is_active ? "#166534" : "#64748b", display: "flex", alignItems: "center", gap: 7 }}>
                <IcoUser />{form.is_active ? "Member is Active" : "Member is Inactive"}
              </span>
              <button type="button" onClick={() => setField("is_active", !form.is_active)}
                style={{ width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer", background: form.is_active ? "#16a34a" : "#e2e8f0", position: "relative", transition: "background .2s", flexShrink: 0 }}>
                <span style={{ position: "absolute", top: 2, left: form.is_active ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.15)", transition: "left .2s" }} />
              </button>
            </div>
          )}

          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
              <span style={{ color: "#6366f1" }}><IcoShield /></span>
              <label style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Permissions</label>
              <span style={{ fontSize: 11.5, background: "#f1f5f9", color: "#64748b", padding: "1px 8px", borderRadius: 20, fontWeight: 600 }}>
                {Object.values(form.permissions).filter(Boolean).length} / {PERM_LABELS.length} enabled
              </span>
            </div>
            <PermissionGrid perms={form.permissions} onChange={setPermission} />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "16px 24px", borderTop: "1px solid #f1f5f9", flexShrink: 0 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: "pointer", background: "#fff", color: "#374151" }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding: "9px 22px", border: "none", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", background: saving ? "#93c5fd" : (isEdit ? "#f59e0b" : "#3b82f6"), color: "#fff", display: "flex", alignItems: "center", gap: 7 }}>
            {saving ? <><IcoLoader size={14} /> Saving…</> : (isEdit ? <><IcoEdit /> Update Member</> : <><IcoPlus /> Add Member</>)}
          </button>
        </div>
      </div>
    </div>
  );
};

const MemberCard = ({ member, onView, onEdit, onDelete }) => {

  return (
    <div className="am-member-card" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", transition: "box-shadow .2s, transform .2s" }}>
      {/* Card top */}
      <div style={{ padding: "16px 16px 12px", borderBottom: "1px solid #f8fafc" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <Avatar name={member.name} size={40} />
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{member.name}</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{member.designation || "—"}</p>
            </div>
          </div>
          <StatusBadge active={member.is_active} />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10 }}>
          <IcoMail />
          <span style={{ fontSize: 12, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{member.email}</span>
        </div>
      </div>

      <div style={{ padding: "10px 16px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
        <PermissionBadge perms={member.permissions} />
      </div>

      <div style={{ display: "flex", gap: 0 }}>
        {[
          { icon: <IcoEye />,   label: "View",   color: "#6366f1", bg: "#f5f3ff", fn: () => onView(member) },
          { icon: <IcoEdit />,  label: "Edit",   color: "#d97706", bg: "#fffbeb", fn: () => onEdit(member) },
          { icon: <IcoTrash />, label: "Remove", color: "#dc2626", bg: "#fff1f2", fn: () => onDelete(member) },
        ].map(({ icon, label, color, bg, fn }, i) => (
          <button key={label} onClick={fn}
            style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "9px 0", background: "#fff", border: "none", borderRight: i < 2 ? "1px solid #f1f5f9" : "none", cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "#64748b", transition: "background .15s, color .15s" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = bg; e.currentTarget.style.color = color; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#64748b"; }}>
            {icon}{label}
          </button>
        ))}
      </div>
    </div>
  );
};

const MemberSkeleton = () => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
    {[1, 2, 3].map((i) => (
      <div key={i} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "16px 16px 12px", borderBottom: "1px solid #f8fafc", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#f1f5f9", animation: "am-shimmer 1.4s infinite" }} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <SkeletonBlock w="60%" h={14} />
              <SkeletonBlock w="40%" h={12} />
            </div>
          </div>
          <SkeletonBlock w="70%" h={12} />
        </div>
        <div style={{ padding: "10px 16px", background: "#f8fafc", display: "flex", gap: 6 }}>
          {[1, 2, 3, 4].map((j) => <SkeletonBlock key={j} w={50} h={20} />)}
        </div>
        <div style={{ height: 38, background: "#f8fafc" }} />
      </div>
    ))}
  </div>
);

const AccountMember = ({ vendorProfileId }) => {
  const [members,      setMembers]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [activeFilter, setActiveFilter] = useState(""); 
  const [showForm,     setShowForm]     = useState(false);
  const [formMode,     setFormMode]     = useState("create");
  const [selectedMember, setSelectedMember] = useState(null);
  const [detailMember,   setDetailMember]   = useState(null);
  const [deleteTarget,   setDeleteTarget]   = useState(null);
  const [deleting,       setDeleting]       = useState(false);

  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 380);
    return () => clearTimeout(timer);
  }, [search]);
  const fetchMembers = useCallback(async (q = debouncedSearch, filter = activeFilter) => {
    setLoading(true);
    try {
      const { data } = await memberApi.list(vendorProfileId, { q, is_active: filter });

      if (data?.status === 401) { toast.error("Session Expired", "Please log in again."); return; }
      if (data?.status === 403) { toast.error("Access Denied", "You don't have permission to view members."); return; }
      if (data?.status >= 400) throw new Error("Load failed");

      const list = data?.results ?? data?.data ?? (Array.isArray(data) ? data : []);
      setMembers(list);
    } catch (err) {
      toast.error(
        err?.name === "TypeError" ? "Network Error" : "Failed to Load",
        err?.name === "TypeError" ? "Unable to reach the server." : "Could not fetch members. Please try again."
      );
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [vendorProfileId, debouncedSearch, activeFilter]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleSearchChange = (val) => {
    setSearch(val);
  };

  const handleFilterChange = (val) => {
    setDebouncedSearch(search);
    setActiveFilter(val);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const loadId = toast.loading("Removing member…");
    try {
      const { data } = await memberApi.delete(vendorProfileId, deleteTarget.id);
      if (data?.status >= 400 && data?.status !== 404) throw new Error("Delete failed");
      toast.dismiss(loadId);
      toast.success("Member Removed", `${deleteTarget.name} has been removed successfully.`);
      setMembers((p) => p.filter((m) => m.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      toast.dismiss(loadId);
      toast.error("Remove Failed", err.message || "Could not remove member.");
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSuccess = (saved) => {
    if (formMode === "edit") {
      setMembers((p) => p.map((m) => (m.id === saved.id ? saved : m)));
    } else {
      setMembers((p) => [saved, ...p]);
    }
  };

  const total    = members.length;
  const active   = members.filter((m) => m.is_active).length;
  const inactive = total - active;

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", color: "#1e293b" }}>
      <style>{`
        @keyframes am-shimmer  { 0%{background-position:200% 0}100%{background-position:-200% 0} }
        @keyframes am-fadeUp   { from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none} }
        @keyframes am-alIn     { from{opacity:0;transform:translateX(24px) scale(.97)}to{opacity:1;transform:none} }
        @keyframes am-alOut    { from{opacity:1}to{opacity:0;transform:translateX(28px) scale(.95)} }
        @keyframes am-spin     { from{transform:rotate(0deg)}to{transform:rotate(360deg)} }
        .am-member-card:hover  { box-shadow:0 6px 20px rgba(0,0,0,.09) !important; transform:translateY(-1px); }
        .am-action-btn:hover   { opacity:.85 !important; }
        * { box-sizing:border-box; }
      `}</style>

      <ToastContainer />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0f172a" }}>Account Members</h2>
            <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "#64748b" }}>Manage vendor account members &amp; permissions</p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button onClick={() => fetchMembers(search, activeFilter)} disabled={loading}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#fff", color: "#374151", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 1px 3px rgba(0,0,0,.05)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}>
            {loading ? <IcoLoader size={15} /> : <IcoRefresh />} Refresh
          </button>
          <button className="am-action-btn"
            onClick={() => { setFormMode("create"); setSelectedMember(null); setShowForm(true); }}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "opacity .15s" }}>
            <IcoPlus /> Add Member
          </button>
        </div>
      </div>

      {!loading && (
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", animation: "am-fadeUp .2s ease" }}>
          {[
            { label: "Total Members",    value: total,    bg: "#f8fafc", border: "#e2e8f0", color: "#0f172a" },
            { label: "Active",           value: active,   bg: "#f0fdf4", border: "#bbf7d0", color: "#16a34a" },
            { label: "Inactive",         value: inactive, bg: "#f8fafc", border: "#e2e8f0", color: "#94a3b8" },
          ].map(({ label, value, bg, border, color }) => (
            <div key={label} style={{ padding: "10px 18px", background: bg, border: `1px solid ${border}`, borderRadius: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".06em", display: "block" }}>{label}</span>
              <span style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}><IcoSearch /></span>
          <input
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by name, email, designation…"
            style={{ width: "100%", padding: "9px 12px 9px 34px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, background: "#fff", color: "#1e293b", outline: "none", boxSizing: "border-box" }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e)  => (e.target.style.borderColor = "#e2e8f0")}
          />
          {search && (
            <button onClick={() => handleSearchChange("")}
              style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 2 }}>
              <IcoClose />
            </button>
          )}
        </div>

        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}><IcoFilter /></span>
          <select value={activeFilter} onChange={(e) => handleFilterChange(e.target.value)}
            style={{ padding: "9px 32px 9px 32px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, background: "#fff", color: "#1e293b", outline: "none", appearance: "none", cursor: "pointer", minWidth: 140 }}>
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}><IcoChevronDown /></span>
        </div>
      </div>

      {loading ? (
        <MemberSkeleton />
      ) : members.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 20px", background: "#fff", borderRadius: 12, border: "1px dashed #e2e8f0", gap: 12, textAlign: "center", animation: "am-fadeUp .2s ease" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg,#f1f5f9,#e2e8f0)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <IcoUsers />
          </div>
          <div>
            <p style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#374151" }}>
              {search || activeFilter ? "No members match your filters" : "No Members Yet"}
            </p>
            <p style={{ margin: 0, fontSize: 13.5, color: "#94a3b8" }}>
              {search || activeFilter ? "Try adjusting your search or filter criteria." : "Add account members to manage access and permissions."}
            </p>
          </div>
          {!search && !activeFilter && (
            <button onClick={() => { setFormMode("create"); setSelectedMember(null); setShowForm(true); }}
              style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 7, padding: "10px 22px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              <IcoPlus /> Add First Member
            </button>
          )}
          {(search || activeFilter) && (
            <button onClick={() => { setSearch(""); setDebouncedSearch(""); setActiveFilter(""); }}
              style={{ marginTop: 4, padding: "9px 20px", background: "#f1f5f9", color: "#374151", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14, animation: "am-fadeUp .2s ease" }}>
          {members.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              onView={(m)   => setDetailMember(m)}
              onEdit={(m)   => { setSelectedMember(m); setFormMode("edit"); setShowForm(true); }}
              onDelete={(m) => setDeleteTarget(m)}
            />
          ))}
        </div>
      )}

      {showForm && (
        <MemberFormModal
          mode={formMode}
          initialData={formMode === "edit" ? selectedMember : null}
          vendorProfileId={vendorProfileId}
          onClose={() => { setShowForm(false); setSelectedMember(null); }}
          onSuccess={handleFormSuccess}
        />
      )}

      {detailMember && (
        <MemberDetailModal
          member={detailMember}
          onClose={() => setDetailMember(null)}
          onEdit={(m) => { setSelectedMember(m); setFormMode("edit"); setShowForm(true); }}
        />
      )}

      {deleteTarget && (
        <ConfirmDeleteModal
          member={deleteTarget}
          loading={deleting}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default AccountMember;